import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import BpkCloseButton from '@skyscanner-internal/backpack-web/bpk-component-close-button';
import {
  withButtonAlignment,
  withRtlSupport,
} from '@skyscanner-internal/backpack-web/bpk-component-icon';
import BpkLocationIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/location';
import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import {
  type DynamicMapStrings,
  type Logo,
  type MarkersDataSource,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/DynamicMapProps';

import { useMBDContext } from '../../common/MBD/useMBDContext';
import MARKERS_DATA_SOURCE from '../types';

import STYLES from './DotMarkerPopup.module.scss';

export type DotMarkerPopupProps = {
  index: number;
  entityName: string;
  logos: Logo[];
  redirectUrl: string;
  strings: DynamicMapStrings;
  address?: string;
  onClose?: () => void;
  markersDataSource?: MarkersDataSource;
};

const LocationIcon = withButtonAlignment(withRtlSupport(BpkLocationIcon));

const DotMarkerPopup = ({
  address = undefined,
  entityName,
  index,
  logos,
  markersDataSource = MARKERS_DATA_SOURCE.MULTIPLE_SUPPLIERS_AND_DESTINATION_NAME_FROM_SUPPLIER,
  onClose = undefined,
  redirectUrl,
  strings,
}: DotMarkerPopupProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const markerLink = strings.markerLink?.replaceAll('@@city@@', entityName);
  const providerLogos = Array.from(
    new Map(
      logos.map((logo) => [`${logo.logoName}-${logo.logoUrl}`, logo]),
    ).values(),
  );

  return (
    <BpkCard
      padded={false}
      atomic={false}
      className={STYLES.DotMarkerPopup__container}
    >
      <div className={STYLES.DotMarkerPopup__header}>
        {onClose && (
          <BpkCloseButton
            className={STYLES.DotMarkerPopup__closeButton}
            label="Close"
            onClick={onClose}
            data-tracking-element-id={`${rawComponentName}_popup_close_${index}`}
            data-tracking-common-params={buildMBDParam(
              `${rawComponentName}_popup_close`,
            )}
          />
        )}
        {/*
        when markersDataSource is multiple_suppliers_and_destination_name_from_supplier, show city name and marker content
         */}
        {markersDataSource ===
          MARKERS_DATA_SOURCE.MULTIPLE_SUPPLIERS_AND_DESTINATION_NAME_FROM_SUPPLIER && (
          <>
            <BpkText tagName="h4" textStyle={TEXT_STYLES.heading4}>
              {entityName}
            </BpkText>
            <BpkText tagName="p" textStyle={TEXT_STYLES.footnote}>
              {strings.markerContent}
            </BpkText>
          </>
        )}
        {/*
        when markersDataSource is supplier_and_pick_up_location_address_from_deal,
        show address when address is exist, show marker content when address is empty
         */}
        {markersDataSource ===
          MARKERS_DATA_SOURCE.SUPPLIER_AND_PICK_UP_LOCATION_ADDRESS_FROM_DEAL &&
          (address ? (
            <div className={STYLES.DotMarkerPopup__address}>
              <LocationIcon className={STYLES.DotMarkerPopup__icon} />
              <BpkText tagName="p" textStyle={TEXT_STYLES.footnote}>
                {address}
              </BpkText>
            </div>
          ) : (
            <BpkText tagName="p" textStyle={TEXT_STYLES.footnote}>
              {strings.markerContent}
            </BpkText>
          ))}
      </div>
      <div className={STYLES.DotMarkerPopup__providerLogoContainer}>
        {providerLogos.map((logo) => (
          <div key={`${logo.logoName}-${logo.logoUrl}`}>
            <img src={logo.logoUrl} alt={logo.logoName} />
          </div>
        ))}
      </div>
      <div className={STYLES.DotMarkerPopup__redirectLink}>
        <BpkLink
          href={redirectUrl}
          rel="nofollow"
          data-tracking-element-id={`${rawComponentName}_popup_link_${index}`}
          data-tracking-common-params={buildMBDParam(
            `${rawComponentName}_popup_link`,
          )}
        >
          <BpkText textStyle={TEXT_STYLES.body}>{markerLink}</BpkText>
        </BpkLink>
      </div>
    </BpkCard>
  );
};

export default DotMarkerPopup;
